import { Controller } from "@hotwired/stimulus"

export default class CatalogAmountController extends Controller {
  static targets = ["preset", "input", "rangeHint"]

  declare readonly presetTargets: HTMLButtonElement[]
  declare readonly inputTarget: HTMLInputElement
  declare readonly rangeHintTarget: HTMLElement
  declare readonly hasRangeHintTarget: boolean

  connect(): void {
    this.updatePresetAvailability()
  }

  selectPreset(event: Event): void {
    const button = event.currentTarget as HTMLButtonElement
    this.inputTarget.value = button.dataset.amount || ""
    this.highlightPreset(button)
    this.revalidate()
  }

  inputChanged(): void {
    this.highlightMatchingPreset()
    this.revalidate()
  }

  brandSetChanged({ params }: { params: { min: number; max: number } }): void {
    this.inputTarget.min = String(params.min)
    this.inputTarget.max = String(params.max)
    this.inputTarget.placeholder = `${params.min}–${params.max}`
    if (this.hasRangeHintTarget) {
      this.rangeHintTarget.textContent = `This catalog supports $${params.min}–$${params.max}.`
    }
    this.updatePresetAvailability()
    this.revalidate()
  }

  private updatePresetAvailability(): void {
    const min = parseInt(this.inputTarget.min, 10)
    const max = parseInt(this.inputTarget.max, 10)
    this.presetTargets.forEach((btn) => {
      const amount = parseInt(btn.dataset.amount || "", 10)
      btn.disabled = amount < min || amount > max
    })
    this.highlightMatchingPreset()
  }

  private highlightMatchingPreset(): void {
    const { value, inRange } = this.currentValue()
    const matched = this.presetTargets.find((btn) => inRange && parseInt(btn.dataset.amount || "", 10) === value) ?? null
    this.highlightPreset(matched)
  }

  private revalidate(): void {
    this.notifyValidity(this.currentValue().inRange)
  }

  private currentValue(): { value: number; inRange: boolean } {
    const value = parseInt(this.inputTarget.value, 10)
    const min = parseInt(this.inputTarget.min, 10)
    const max = parseInt(this.inputTarget.max, 10)
    return { value, inRange: !Number.isNaN(value) && value >= min && value <= max }
  }

  private highlightPreset(active: HTMLButtonElement | null): void {
    this.presetTargets.forEach((btn) => {
      btn.classList.toggle("btn-primary", btn === active)
      btn.classList.toggle("btn-primary-subtle", btn !== active)
    })
  }

  private notifyValidity(valid: boolean): void {
    this.dispatch("validity-changed", { detail: { valid }, bubbles: true, cancelable: false })
  }
}
